import { Badge, notification } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { Howl } from "howler";
import { useNotifications } from "../hooks/useNotifications";
import notificationSound from "../assets/audio/notification.wav";
import axios from "../utils/axios";
import { useNavigate } from "react-router-dom";

const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [audioReady, setAudioReady] = useState(false); // Tracks if audio context is unlocked
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();
  const authDataString = localStorage.getItem("adminauthor");
  const auth = authDataString ? JSON.parse(authDataString) : null;
  const adminId = auth?.admin?._id;

  // Initialize Howler sound
  const sound = new Howl({
    src: [notificationSound],
    volume: 0.5,
    preload: true, // Preload for instant playback

    onloaderror: (id, err) => {
      console.error("Failed to load notification sound:", err);
    },
  });

  useEffect(() => {
    // Fetch notifications on component mount
    const fetchNotifications = async () => {
      try {
        const response = await axios.get(
          `/api/v1/user/notifications-admin/${adminId}`
        );
        setUnreadCount(response.data.unreadCount);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    if (adminId) {
      fetchNotifications();
    }

    // Attempt to unlock audio context on mount
    const unlockAudio = () => {
      if (!audioReady) {
        sound.play(); // Attempt to play (will be silent if context is locked)
        sound.stop(); // Stop immediately to unlock context
        setAudioReady(true);
      }
    };

    // Listen for user interaction to unlock audio
    const handleInteraction = () => {
      if (!audioReady) {
        sound.play();
        sound.stop();
        setAudioReady(true);
        console.log("Audio context unlocked via user interaction");
        // Remove listeners after unlocking
        window.removeEventListener("click", handleInteraction);
        window.removeEventListener("keydown", handleInteraction);
      }
    };

    window.addEventListener("click", handleInteraction);
    window.addEventListener("keydown", handleInteraction);
    unlockAudio(); // Try unlocking on mount

    return () => {
      window.removeEventListener("click", handleInteraction);
      window.removeEventListener("keydown", handleInteraction);
      sound.unload(); // Cleanup sound on unmount
    };
  }, [audioReady, adminId]);

  // Handle socket notifications
  useNotifications((data) => {
    setUnreadCount((prev) => prev + 1);

    // Show Ant Design notification
    api.info({
      message: data.title || "New Notification",
      description: data.message || "",
      placement: "topRight",
      duration: 15,
    });

    // Play sound if audio context is unlocked
    if (audioReady) {
      sound.play();
    } else {
      console.log("Audio not played: waiting for user interaction");
      // Attempt to unlock again
      sound.play();
      sound.stop();
      setAudioReady(true);
    }
  });

  const handleBellClick = async () => {
    // Ensure audio is unlocked on click
    if (!audioReady) {
      sound.play();
      sound.stop();
      setAudioReady(true);
      console.log("Audio context unlocked via bell click");
    }

    // Redirect to notifications page
    navigate("/dashboard/admin/notifications");

    // Mark all notifications as read
    try {
      await axios.patch(
        `/api/v1/user/notifications/mark-all-read-admin/${adminId}`
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  return (
    <>
      {contextHolder}
      <div style={{ cursor: "pointer" }} onClick={handleBellClick}>
        <Badge
          count={unreadCount}
          style={{
            backgroundColor: "#096b68",
            color: "#fff",
            fontWeight: "bold",
            fontSize: 11,
            padding: "0 6px",
          }}
        >
          <BellOutlined className="notification-icon" />
        </Badge>
      </div>
    </>
  );
};

export default NotificationBell;
